import { cn } from '@/lib/utils'
import {
  Database,
  FileText,
  Search,
  Package,
  Users,
  AlertCircle,
  Store,
} from 'lucide-react'

export type EmptyStateVariant =
  | 'default'
  | 'search'
  | 'data'
  | 'files'
  | 'products'
  | 'vendors'
  | 'users'
  | 'error'

export interface EmptyStateProps {
  title?: string
  description?: string
  icon?: React.ReactNode
  variant?: EmptyStateVariant
  action?: React.ReactNode
  className?: string
  iconClassName?: string
  titleClassName?: string
  descriptionClassName?: string
  minHeight?: string
}

// Default icons for each variant
const defaultIcons: Record<EmptyStateVariant, React.ReactNode> = {
  default: <Database className='h-12 w-12 text-muted-foreground' />,
  search: <Search className='h-12 w-12 text-muted-foreground' />,
  data: <Database className='h-12 w-12 text-muted-foreground' />,
  files: <FileText className='h-12 w-12 text-muted-foreground' />,
  products: <Package className='h-12 w-12 text-muted-foreground' />,
  vendors: <Store className='h-12 w-12 text-muted-foreground' />,
  users: <Users className='h-12 w-12 text-muted-foreground' />,
  error: <AlertCircle className='h-12 w-12 text-muted-foreground' />,
}

// Default titles for each variant
const defaultTitles: Record<EmptyStateVariant, string> = {
  default: 'No data available',
  search: 'No results found',
  data: 'No data available',
  files: 'No files found',
  products: 'No products found',
  vendors: 'No vendors found',
  users: 'No users found',
  error: 'Something went wrong',
}

// Default descriptions for each variant
const defaultDescriptions: Record<EmptyStateVariant, string> = {
  default: 'There are no items to display at the moment.',
  search: 'Try adjusting your search criteria or filters.',
  data: 'There are no items to display at the moment.',
  files: 'No files match your current criteria.',
  products: 'No products match your current criteria.',
  vendors: 'Vendors will appear here.',
  users: 'No users match your current criteria.',
  error: 'An error occurred while loading the data.',
}

export function EmptyState({
  title,
  description,
  icon,
  variant = 'default',
  action,
  className,
  iconClassName,
  titleClassName,
  descriptionClassName,
  minHeight = '500px',
}: EmptyStateProps) {
  const displayIcon = icon || defaultIcons[variant]
  const displayTitle = title || defaultTitles[variant]
  const displayDescription = description || defaultDescriptions[variant]

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center p-8 text-center',
        className
      )}
      style={{ minHeight }}
    >
      <div className={cn('mb-4', iconClassName)}>{displayIcon}</div>

      <h3
        className={cn(
          'mt-4 text-lg font-semibold text-foreground',
          titleClassName
        )}
      >
        {displayTitle}
      </h3>

      <p
        className={cn(
          'mt-2 text-sm text-muted-foreground max-w-sm',
          descriptionClassName
        )}
      >
        {displayDescription}
      </p>

      {action && <div className='mt-6'>{action}</div>}
    </div>
  )
}

export default EmptyState
